import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragAndDropFile,
  FormFieldInput,
  LoadingIndicator,
  TaxTypeIndia,
  ToggleTaxTypeIndia,
  validateGst,
  validatPan,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  FieldError,
  FieldErrors,
  Resolver,
  ResolverOptions,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleX, faFileArrowUp } from '@fortawesome/pro-light-svg-icons';
import { State } from 'country-state-city';
import { useConfiguration } from '@foxtrotplatform/platform-micro-site-core';

import { SignUpStep } from '../../common/provider/SignUpContext';
import { useSignUp } from '../../common/hooks/useSignUp';
import { uploadTaxDetails } from '../../common/api/sign-up';
import { TaxIndiaForm } from './types';
import { ApiErrorResponse } from '../../common/api/types';
import {
  acceptedTaxFileTypes,
  allowedTotalTaxFileSizeInBytes,
  GST_STATE,
  SIGN_UP_BILLING_DETAIL_FORM,
  SIGN_UP_TAX_ID,
  SIGN_UP_TAX_TYPE,
  whitelistedStatusCodes,
} from '../../common/constants';
import { ActionBar } from './ActionBar';
import { Header } from './Header';
import { stepRouteMapping } from '../signUpUtility';
import { InputLabelTitle } from './InputLabelTitle';

export const taxIndiaFormResolver: Resolver<TaxIndiaForm> = async (
  values,
  { setGstAddress, gstValidationUrl }
) => {
  const errors = {} as FieldErrors<TaxIndiaForm>;

  if (values.taxType === TaxTypeIndia.GST && !values.gstId) {
    errors.gstId = {
      message: 'PAN or GST is required',
    } as FieldError;
  } else if (values.taxType === TaxTypeIndia.PAN && !values.panId) {
    errors.panId = {
      message: 'PAN or GST is required',
    } as FieldError;
  } else if (values.taxType === TaxTypeIndia.GST) {
    const message = await validateGst(gstValidationUrl, values.gstId);
    if (typeof message === 'string') {
      errors.gstId = { message } as FieldError;
    } else if (message.valid && setGstAddress) {
      // Slap in fix: TODO: change to more permanent solution original [message.state]
      const code = values.gstId.substring(0, 2);
      const state = State.getStateByCodeAndCountry(GST_STATE[code], 'IN')?.name;
      setGstAddress({ state, pinCode: message.pinCode });
    }
  } else {
    const message = await validatPan(values.panId);
    if (typeof message === 'string') {
      errors.panId = { message } as FieldError;
    }
  }
  return { values, errors };
};

export const TaxIndiaFormComponent: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { configuration } = useConfiguration();
  const { baseApiUrl } = configuration;
  const { setSignUpStep, setErrorMessage, setGstAddress, formId } = useSignUp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadedFileList, setFileList] = useState<File[]>();
  const [tempGstTaxId, setTempGstTaxId] = useState<string>();
  const [tempPanTaxId, setTempPanTaxId] = useState<string>();
  const [selectedTaxType, setSelectedTaxType] = useState<
    TaxTypeIndia | undefined
  >();

  const localTaxId = useMemo(() => {
    const rawData = sessionStorage.getItem(SIGN_UP_TAX_ID);
    if (rawData) {
      const dataObject: string = JSON.parse(rawData);
      return dataObject;
    }
    return '';
  }, []);

  const getTaxType = () => {
    const rawData = sessionStorage.getItem(SIGN_UP_TAX_TYPE);
    if (rawData) {
      return JSON.parse(rawData);
    }
    return TaxTypeIndia.GST;
  };

  const localTaxType = getTaxType();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    trigger,
    formState: { errors, isValid, isValidating },
  } = useForm<TaxIndiaForm>({
    mode: 'onChange',
    defaultValues: {
      gstId: localTaxType === TaxTypeIndia.GST ? localTaxId : '',
      panId: localTaxType === TaxTypeIndia.PAN ? localTaxId : '',
      taxType: getTaxType(),
    },
    resolver: (values) =>
      taxIndiaFormResolver(
        values,
        {
          setGstAddress,
          gstValidationUrl: `${baseApiUrl}/cache/gst/`,
        },
        {} as ResolverOptions<TaxIndiaForm>
      ),
  });
  const taxType = watch('taxType');
  const gstId = watch('gstId');
  const panId = watch('panId');

  useEffect(() => {
    if (taxType === TaxTypeIndia.GST) {
      setTempGstTaxId(gstId);
    } else if (taxType === TaxTypeIndia.PAN) {
      setTempPanTaxId(panId);
    }
    setSelectedTaxType(TaxTypeIndia[taxType]);
  }, [panId, gstId, taxType, tempGstTaxId, tempPanTaxId]);

  const triggerTaxInput = useCallback(
    (taxType: TaxTypeIndia) => {
      if (taxType === TaxTypeIndia.GST && tempGstTaxId) {
        setValue('gstId', tempGstTaxId);
        trigger('gstId');
      } else if (taxType === TaxTypeIndia.GST && !tempGstTaxId) {
        setValue('gstId', '');
        if (tempPanTaxId) trigger('gstId');
      } else if (taxType === TaxTypeIndia.PAN && tempPanTaxId) {
        setValue('panId', tempPanTaxId);
        trigger('panId');
      } else if (taxType === TaxTypeIndia.PAN && !tempPanTaxId) {
        setValue('panId', '');
        if (tempGstTaxId) trigger('panId');
      }
    },
    [tempGstTaxId, tempPanTaxId, setValue, trigger]
  );

  useEffect(() => {
    if (selectedTaxType && selectedTaxType !== taxType) {
      triggerTaxInput(TaxTypeIndia[taxType]);
    }
  }, [taxType, selectedTaxType, triggerTaxInput]);

  const onSubmit = handleSubmit(
    async ({ panId, gstId, taxType }: TaxIndiaForm) => {
      try {
        setIsLoading(true);
        const taxId = taxType === TaxTypeIndia.GST ? gstId : panId;
        let response = await uploadTaxDetails(
          formId as string,
          taxId,
          TaxTypeIndia[taxType],
          uploadedFileList
        );
        if (!response?.validation?.valid && response?.validation?.violations) {
          response.validation.violations.forEach((violation) => {
            if (taxType === TaxTypeIndia.GST) {
              setError('gstId', {
                message: violation.violation,
              });
            } else {
              setError('panId', {
                message: violation.violation,
              });
            }
          });
        } else {
          sessionStorage.setItem(SIGN_UP_TAX_ID, JSON.stringify(taxId));
          sessionStorage.setItem(SIGN_UP_TAX_TYPE, JSON.stringify(taxType));
          if (localTaxId !== taxId) {
            sessionStorage.removeItem(SIGN_UP_BILLING_DETAIL_FORM);
          }
          setSignUpStep(SignUpStep.billingDetails);
          navigate(stepRouteMapping[SignUpStep[SignUpStep.billingDetails]]);
        }
        setIsLoading(false);
      } catch (e: any) {
        if (whitelistedStatusCodes.includes(e?.request?.status)) {
          const errorResponse: ApiErrorResponse = JSON.parse(
            e.request.response
          );
          setErrorMessage(errorResponse.error.description);
        } else {
          setErrorMessage('Please go back to the form and try again');
        }
        setIsLoading(false);
      }
    }
  );

  const manageFiles = useCallback(
    (fileList: FileList | null) => {
      if (fileList && fileList.length > 0) {
        const file = fileList[0];
        if (fileList.length > 2) {
          setErrorMessage(`Only two files can be uploaded`);
        } else if (file.size > allowedTotalTaxFileSizeInBytes) {
          setErrorMessage(`file too large`);
        } else if (
          acceptedTaxFileTypes.findIndex((type) => type === file.type) > -1
        ) {
          if (uploadedFileList && uploadedFileList?.length >= 1) {
            setFileList([file, uploadedFileList[0]]);
          } else {
            setFileList([file]);
          }
        } else {
          setErrorMessage(`${file.type} is not a supported file type.`);
        }
      }
    },
    [setFileList, setErrorMessage, uploadedFileList]
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      manageFiles(e.target.files);
    },
    [manageFiles]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      manageFiles(e.dataTransfer.files);
    },
    [manageFiles]
  );

  const onRemoveFile = useCallback(
    (name: string) => () => {
      if (uploadedFileList) {
        const updatedFileArray = uploadedFileList.filter(
          (file) => file.name !== name
        );
        if (updatedFileArray && updatedFileArray.length > 0) {
          setFileList([...updatedFileArray]);
        } else {
          setFileList(undefined);
        }
      }
    },
    [uploadedFileList]
  );

  const renderFileComponent = useCallback(() => {
    if (uploadedFileList && uploadedFileList.length > 0) {
      return (
        <div className="flex flex-col gap-y-4">
          <span className="font-bold">Files</span>
          {uploadedFileList.map((file) => {
            return (
              <div key={file.name} className="flex flex-row w-full gap-x-4">
                <div className="flex flex-row items-center border border-secondary-800 justify-between rounded-md gap-x-4 w-full px-3 py-2">
                  <FontAwesomeIcon
                    className="fa-regular text-secondary-800"
                    icon={faFileArrowUp}
                  />
                  <span className="text-xs text-slate-700">{file.name}</span>
                </div>
                <button>
                  <FontAwesomeIcon
                    icon={faCircleX}
                    onClick={onRemoveFile(file.name)}
                    className="text-neutral-100"
                  />
                </button>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  }, [uploadedFileList, onRemoveFile]);

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:h-[100vh] w-full items-center content-between">
      <Header
        subtitle="We require either GST or PAN number, enter one to continue."
        title="Complete your KYC"
      />
      <form className="flex-1 grid grid-cols-12 w-full px-6 xl:px-0 pb-8 font-opensans lg:overflow-auto mb-24 lg:mb-56">
        <div className="col-start-1 xl:col-start-2 col-span-10 flex flex-col gap-4 h-full">
          <ToggleTaxTypeIndia control={control} inputKey="taxType" />
          <div className="pt-12">
            {taxType === TaxTypeIndia.GST ? (
              <FormFieldInput
                errors={errors}
                inputKey="gstId"
                className="lg:w-11/12 md:w-11/12 sm:w-full xs:w-full pb-4"
                register={register}
                registerProps={{
                  required: true,
                }}
                isValidating={isValidating}
                validationMessage="Checking GST"
                inputClassName="shadow"
                testId="gstId-input"
                title={
                  <InputLabelTitle
                    title={`Verify ${taxType} number`}
                    hasError={Boolean(errors?.gstId)}
                    required
                  />
                }
              />
            ) : (
              <FormFieldInput
                errors={errors}
                inputKey="panId"
                className="lg:w-11/12 md:w-11/12 sm:w-full xs:w-full pb-4"
                register={register}
                registerProps={{
                  required: true,
                }}
                inputClassName="shadow"
                testId="panId-input"
                title={
                  <InputLabelTitle
                    title={`Verify ${taxType} number`}
                    hasError={Boolean(errors?.panId)}
                    required
                  />
                }
              />
            )}
            <div className="lg:w-11/12 md:w-11/12 sm:w-full xs:w-full">
              <span className="text-xs text-slate-700">
                Please enter a valid {taxType} number and then add documents.
              </span>
            </div>
          </div>
          <div className="flex flex-row justify-center items-center w-5/6 py-4">
            <DragAndDropFile
              acceptedFileTypes={acceptedTaxFileTypes}
              multipleFiles={true}
              onChange={onChange}
              onDrop={onDrop}
              linkText={`Upload ${taxType} Files(PNG, JPEG, PDF)`}
              subText="Or drag and drop files here ( 2 documents and 10 MB max)"
            />
          </div>
          {renderFileComponent()}
        </div>
      </form>
      <ActionBar onSubmit={onSubmit} isValid={isValid} />
    </div>
  );
};
